import { useCallback } from "react";
import { RoomEventHandlerMap } from "../../video-sdk/core/room/types";
import {
  filterOutByParticipantIdentity,
  hasParticipantById as hasParticipantById,
  hasParticipantBySid,
  remoteParticipantToDiagnosticInfo,
} from "../../video-sdk/core/utils";
import {
  participantsAtom,
  remoteParticipantConnectionStatesByIdAtom,
} from "../../video-sdk/core/room/participant/atoms";
import { useAtomCallback } from "jotai/utils";
import { sendLoggingEvents, TrackingEvents } from "../../events";
import { noop } from "../../utils";
import { useSetAtom } from "jotai";
import { ParticipantConnectionState } from "../../video-sdk/core/room/participant/types";
import { logger } from "../../datadog/logger";
import { RemoteParticipant } from "../../video-sdk/core/room/participant/RemoteParticipant";
import { parseIdentity } from "../../video-sdk/core/room/participant/utils";

type RoomParticipantConnectedEventHandler =
  RoomEventHandlerMap["participantConnected"];

export type ParticipantConnectedCallbackOpts = {
  /**
   * Skips the guard to prevent handling of duplicate participant connections.
   * Specify this option for updates e.g. reconnects that don't involve a user
   * joining the room anew.
   */
  isUpdate?: boolean;
  onParticipantInitialConnect?: RoomParticipantConnectedEventHandler;
};

export function useParticipantConnectedEventHandlerCallback() {
  const getParticipants = useAtomCallback(
    useCallback((get) => get(participantsAtom), []),
  );
  const setParticipants = useSetAtom(participantsAtom);

  const setRemoteParticipantConnectionStatesById = useSetAtom(
    remoteParticipantConnectionStatesByIdAtom,
  );

  return useCallback(
    (
      participant: RemoteParticipant,
      {
        isUpdate,
        onParticipantInitialConnect,
      }: ParticipantConnectedCallbackOpts = {},
    ) => {
      const isAlreadyConnected = hasParticipantBySid(
        getParticipants(),
        participant,
      );
      if (isAlreadyConnected && !isUpdate) {
        logger.warn(
          "Skipping already-connected participant",
          remoteParticipantToDiagnosticInfo(participant),
        );
        return;
      }

      const participantId = parseIdentity(participant.identity).id;
      const isExistingId = hasParticipantById(getParticipants(), participant);

      if (isUpdate && !isExistingId) {
        logger.warn(
          "Participant connection update received for unknown identity",
          remoteParticipantToDiagnosticInfo(participant),
        );
        return;
      }

      const [loggingEvent, logMessage, connectedParticipantCallback] =
        isExistingId
          ? [
              // Rejoin (diff SID, same identity) or reconnect (same SID)
              TrackingEvents.REMOTE_PARTICIPANT_RECONNECTED,
              "Remote participant reconnected",
              noop,
            ]
          : [
              TrackingEvents.REMOTE_PARTICIPANT_CONNECTED,
              "New remote participant connected",
              onParticipantInitialConnect,
            ];

      sendLoggingEvents(
        loggingEvent,
        {
          participant: remoteParticipantToDiagnosticInfo(participant),
        },
        { logLevel: "info", message: logMessage },
      );
      connectedParticipantCallback?.(participant);
      setParticipants((prev) => {
        return [
          ...filterOutByParticipantIdentity(prev, participant.identity),
          participant,
        ];
      });
      setRemoteParticipantConnectionStatesById((prev) => ({
        ...prev,
        [participantId]: ParticipantConnectionState.Connected,
      }));
    },
    [
      getParticipants,
      setParticipants,
      setRemoteParticipantConnectionStatesById,
    ],
  );
}
